import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import AppContext from '@/context/appContext'
import {
  ELIGIBILITY_QUERY,
  useCreateFarmClusters,
  useDeleteFarmClusters,
  useEligibilityCheck,
  useExclusionAreas,
  useFarmClusters,
  useFarmQuantificationUnits,
  useSamplingAreas,
} from '@/hooks/data'
import {
  Cluster,
  ClusterRun,
  EligibilityArea,
  FarmQuantificationUnit,
  ToGeoJSON,
  UnassignedPaddock,
} from '@/types/apiModels'
import { PROGRAM_IDS } from '@/utils/constants'
import { isEligibilityEnabled } from '@/utils/programConfig/areaSubtractions'
import { useEligibilityStatus } from '../EligibilityCheck/useEligibilityStatus'

interface StratificationContextType {
  samplingAreas: ToGeoJSON[] | null
  isLoadingSamplingAreas: boolean
  clusters: Cluster[] | null
  isLoadingClusters: boolean
  latestClusterRun: ClusterRun | null
  createClusters: () => Promise<void>
  deleteClusters: () => Promise<void>
  isDeletingClusters: boolean
  quantificationUnits: FarmQuantificationUnit[] | null
  unassignedPaddocks: UnassignedPaddock[] | null
  hasPlan: boolean
  isLoadingQUs: boolean
  refetchQUs: () => Promise<unknown>
  isEligibilityCheckRequired: boolean
  eligibilityCheckAreas: EligibilityArea[] | null
  hasEligibilityCheck: boolean
  isLoadingEligibilityCheck: boolean
  isRunningEligibility: boolean
  isEligibilityCheckError: boolean
  refetchEligibilityCheck: () => Promise<unknown>
  isFetchingEligibilityCheck: boolean
  isMissingExclusionAreas: boolean
  hiddenSamplingAreaQUIds: ReadonlySet<string>
  toggleSamplingAreasVisibility: (quId: string) => void
}

const StratificationContext = createContext<StratificationContextType | undefined>(undefined)

export const useStratificationContext = () => {
  const context = useContext(StratificationContext)
  if (!context) {
    throw new Error('useStratificationContext must be used within a StratificationProvider')
  }
  return context
}

export const StratificationProvider: FC<PropsWithChildren> = ({ children }) => {
  const { currentFarm, programConfig } = useContext(AppContext)
  const { data: samplingAreasData, isLoading: isLoadingSamplingAreas } = useSamplingAreas({
    farmId: currentFarm?.id,
  })
  const samplingAreas = samplingAreasData ?? null
  const {
    data: clustersData,
    isLoading: isLoadingClusters,
    latestRun: latestClusterRun,
  } = useFarmClusters({
    farmId: currentFarm?.id,
  })
  const clusters = clustersData ?? null
  const { mutateAsync: createClusters } = useCreateFarmClusters({ farmId: currentFarm?.id })
  const { mutateAsync: deleteClusters, isPending: isDeletingClusters } = useDeleteFarmClusters({
    farmId: currentFarm?.id,
  })
  const {
    quantificationUnits,
    unassignedPaddocks,
    hasPlan,
    isLoading: isLoadingQUs,
    refetch: refetchQUs,
  } = useFarmQuantificationUnits({
    farmId: currentFarm?.id,
  })
  const isEligibilityCheckRequired = isEligibilityEnabled(programConfig)
  const { isRunningEligibility } = useEligibilityStatus(currentFarm?.id)
  const {
    eligibilityCheckAreas,
    hasEligibilityCheck,
    isLoading: isLoadingEligibilityCheck,
    isError: isEligibilityCheckError,
    refetchEligibilityCheck,
    isFetchingEligibilityCheck,
  } = useEligibilityCheck({
    farmId: currentFarm?.id,
    query: ELIGIBILITY_QUERY,
    isCheckRunning: isRunningEligibility,
  })
  // ReTerra only, because only its cluster perimeter subtracts manual (forest-type) exclusion areas.
  // The warning fires when the farm has no exclusion areas of any type, not just forest ones.
  const isReTerraFarm = currentFarm?.programId === PROGRAM_IDS.RETERRA
  const { data: exclusionAreas, isSuccess: hasLoadedExclusionAreas } = useExclusionAreas({
    farmId: isReTerraFarm ? currentFarm.id : undefined,
  })
  // Gated on isSuccess, not just an empty array: React Query keeps the last data when a refetch
  // fails, so an errored read can still carry [].
  const isMissingExclusionAreas =
    isReTerraFarm && hasLoadedExclusionAreas && exclusionAreas.length === 0

  const [hiddenSamplingAreaQUIds, setHiddenSamplingAreaQUIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const toggleSamplingAreasVisibility = useCallback((quId: string) => {
    setHiddenSamplingAreaQUIds(prev => {
      const next = new Set(prev)
      if (!next.delete(quId)) next.add(quId)
      return next
    })
  }, [])

  const contextValue = useMemo(
    () => ({
      samplingAreas,
      isLoadingSamplingAreas,
      clusters,
      isLoadingClusters,
      latestClusterRun,
      createClusters,
      deleteClusters,
      isDeletingClusters,
      quantificationUnits,
      unassignedPaddocks,
      hasPlan,
      isLoadingQUs,
      refetchQUs,
      isEligibilityCheckRequired,
      eligibilityCheckAreas,
      hasEligibilityCheck,
      isLoadingEligibilityCheck,
      isRunningEligibility,
      isEligibilityCheckError,
      refetchEligibilityCheck,
      isFetchingEligibilityCheck,
      isMissingExclusionAreas,
      hiddenSamplingAreaQUIds,
      toggleSamplingAreasVisibility,
    }),
    [
      samplingAreas,
      isLoadingSamplingAreas,
      clusters,
      isLoadingClusters,
      latestClusterRun,
      createClusters,
      deleteClusters,
      isDeletingClusters,
      quantificationUnits,
      unassignedPaddocks,
      hasPlan,
      isLoadingQUs,
      refetchQUs,
      isEligibilityCheckRequired,
      eligibilityCheckAreas,
      hasEligibilityCheck,
      isLoadingEligibilityCheck,
      isRunningEligibility,
      isEligibilityCheckError,
      refetchEligibilityCheck,
      isFetchingEligibilityCheck,
      isMissingExclusionAreas,
      hiddenSamplingAreaQUIds,
      toggleSamplingAreasVisibility,
    ],
  )

  return (
    <StratificationContext.Provider value={contextValue}>{children}</StratificationContext.Provider>
  )
}
